import { all, fork, put, takeEvery, call, select } from 'redux-saga/effects';
import * as actionTypes from 'appRedux/actions/taxInvoice/taxInvoiceActionType';
import * as actionCreators from 'appRedux/actions/taxInvoice';
import { userSignOut } from 'appRedux/actions/Auth';
import { API_URL_V1, API_PMS_HEADERS } from 'constants/ApiSetting';
import { APIPostRequest } from 'util/connection';
import { getItems } from 'util/localStorage';
import { ApiResponse } from 'constants/ActionTypes';
import { TaxInvoiceResponse } from 'routes/main/taxInvoice/types';
import { AppState } from 'appRedux/reducers/ReducerState';

function* taxInvoiceRequestProcess({ payload }: actionTypes.TaxInvoiceRequestAction): Generator<any, void, any> {
  try {
    // Check if SALESTAX is enabled before making API call
    const systemConfig: any = yield select((state: AppState) => state.systemConfig);
    const isSalesTaxEnabled = systemConfig?.dataSource?.interfaces?.SALESTAX === true;

    if (!isSalesTaxEnabled) {
      console.log('Tax Invoice API: SALESTAX is disabled, skipping API call');
      // Return empty success response instead of error to prevent UI issues
      yield put(actionCreators.taxInvoiceSuccess({
        data: {
          result_code: '0',
          result_msg: 'SALESTAX is disabled',
          msg_code: '00',
          result_data: {
            items: [],
            paging: {
              current: 1,
              limit: 20,
              total_rows: 0,
              row_count: 0,
            }
          }
        },
        status: 200,
        message: 'OK'
      }));
      return;
    }

    // ดึง token จาก localStorage
    const { access_token } = yield call(getItems, ['access_token']);

    if (!access_token) {
      console.error('Tax Invoice API: Access token is missing');
      yield put(actionCreators.taxInvoiceError('Authentication token is missing. Please login again.'));
      return;
    }

    // ใช้ API URL ตาม pattern ของ salestax APIs
    const url = `${API_URL_V1}pms/rpc/salestax/get_issued_tax_invoices`;

    // ใช้ API_PMS_HEADERS ตาม pattern ของ cashier sagas อื่นๆ
    API_PMS_HEADERS.headers.Authorization = `Bearer ${access_token}`;

    const response: any = yield call(APIPostRequest, url, payload, API_PMS_HEADERS);

    // APIPostRequest return response.data โดยตรง
    // Response structure จาก server: { data: { result_code, result_msg, result_data }, status, message }
    // แต่ APIPostRequest return response.data โดยตรง ดังนั้น response = { data: {...}, status, message }

    // ตรวจสอบ response structure
    const responseData = response?.data || response;
    const hasResultCode = responseData?.result_code !== undefined;
    const hasResultData = responseData?.result_data !== undefined;
    const resultCode = responseData?.result_code;
    const items = responseData?.result_data?.items || [];
    const paging = responseData?.result_data?.paging;

    // ตรวจสอบว่า response มี status หรือไม่
    // ถ้ามี status และเป็น 200 หรือไม่มี status แต่มี result_code = '0' แสดงว่าสำเร็จ
    const isSuccess = (response?.status === 200 || !response?.status) && resultCode === '0';

    if (isSuccess && hasResultCode && hasResultData) {
      // สร้าง TaxInvoiceResponse structure ที่ตรงกับ type definition
      const taxInvoiceResponse: TaxInvoiceResponse = {
        data: {
          result_code: responseData.result_code,
          result_msg: responseData.result_msg || 'Success',
          msg_code: responseData.msg_code,
          result_data: {
            items: items,
            paging: paging || {
              current: 1,
              limit: 20,
              total_rows: 0,
              row_count: 0,
            },
          },
        },
        status: response?.status || 200,
        message: response?.message || 'OK',
      };

      yield put(actionCreators.taxInvoiceSuccess(taxInvoiceResponse));
    } else {
      // Error case
      const errorMessage = responseData?.result_msg || response?.message || 'Failed to fetch tax invoices';

      yield put(actionCreators.taxInvoiceError(errorMessage));

      if (response?.status === 403) {
        // Force signout if unauthorized or already logged out
        const responseDataForError = response?.data || response;
        const errorMsg = responseDataForError?.result_msg || response?.message || errorMessage;
        const errorData = responseDataForError?.data;

        const isAlreadyLoggedOut =
          errorMsg === 'Already logged out' ||
          errorMsg === 'Invalid token, authentication required' ||
          errorData === null ||
          errorData === undefined;

        // Signout if already logged out or if data exists (unauthorized)
        if (isAlreadyLoggedOut || errorData) {
          yield put(userSignOut());
        }
      }
    }
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
    yield put(actionCreators.taxInvoiceError(errorMessage, error));
  }
}

function* taxInvoiceRequest() {
  yield takeEvery(actionTypes.TAX_INVOICE_REQUEST, taxInvoiceRequestProcess);
}

export default function* rootSaga() {
  yield all([fork(taxInvoiceRequest)]);
}

